import React, { useState } from 'react';
import { Box } from '@mui/material';
import AutoStoriesSharpIcon from '@mui/icons-material/AutoStoriesSharp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

export default function LessonSidebar({ 
  course, 
  activeLesson, 
  onSelectLesson,
  onHeaderClick
}) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const toggleMobileMenu = (e) => {
    e.stopPropagation();
    setIsMobileOpen(!isMobileOpen);
  };

  const handleHeaderClick = (e) => {
    e.stopPropagation();
    if (onHeaderClick) {
      onHeaderClick();
    }
  };

  return (
    <Box sx={{
      background: '#FAFAFA',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      p: '15px',
      width: '100%',
      position: 'relative',
      fontFamily: 'Arial, sans-serif',
      my: '20px',
      height: 'fit-content',
      boxSizing: 'border-box',
      '@media (min-width: 1024px)': {
        width: 300,
        position: 'sticky',
        top: '90px',
        p: '5px',
        my: '50px'
      }
    }}>
      {/* Header Section */}
      <Box 
        onClick={handleHeaderClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: '16px',
          mt: '8px',
          position: 'relative',
          pl: '15px',
          minWidth: 0,
          justifyContent: 'space-between',
          cursor: onHeaderClick ? 'pointer' : 'default',
          '@media (min-width: 1024px)': {
            justifyContent: 'flex-start',
            pl: '10px'
          }
        }}
      >
        {/* Book Icon + Title */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: '#ffffff',
            color: '#f44336',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: '8px',
            fontSize: '18px',
            flexShrink: 0
          }}>
            <AutoStoriesSharpIcon fontSize="small" />
          </Box>

          <Box sx={{
            minWidth: 0,
            fontSize: '14px'
          }}>
            <Box sx={{ fontSize: '14px', color: '#666' }}>Courses</Box>
            <Box component="h4" sx={{ 
              m: 0,
              fontSize: '14px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {course?.title}
            </Box>
          </Box>
        </Box>
        
        {/* Toggle Button (shown on mobile and tablet) */}
        <Box 
          onClick={toggleMobileMenu}
          sx={{
            display: { xs: 'flex', lg: 'none' },
            alignItems: 'center',
            justifyContent: 'center',
            color: '#666',
            ml: '10px',
            cursor: 'pointer'
          }}
        >
          {isMobileOpen ? 
            <ExpandLessIcon fontSize="large" /> : 
            <ExpandMoreIcon fontSize="large" />}
        </Box>
      </Box>

      {/* Lessons Container */}
      <Box sx={{
        background: '#fff',
        borderRadius: '6px',
        mt: '8px',
        pt: '10px',
        pl: '10px',
        pr: '10px',
        display: { xs: isMobileOpen ? 'block' : 'none', lg: 'block' },
        '@media (min-width: 1024px)': {
          display: 'block !important',
          pt: '10px',
          pl: '10px',
          pr: '10px'
        }
      }}>
        <Box sx={{ position: 'relative', pb: '24px' }}>
          {/* Decorative Vertical Line */}
          <Box sx={{
            position: 'absolute',
            top: '-35px',
            left: '19px',
            width: '1px',
            height: '100%',
            background: 'repeating-linear-gradient(to bottom, #ddd, #ddd 4px, transparent 4px, transparent 8px)',
            zIndex: 0
          }} />

          {/* Lessons List */}
          <Box component="ul" sx={{ 
            listStyle: 'none',
            p: 0,
            m: 0
          }}>
            {course?.lessons?.map((lesson, index) => (
              <Box 
                component="li"
                key={lesson.id}
                onClick={() => onSelectLesson(lesson.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  position: 'relative',
                  pb: '24px',
                  cursor: 'pointer',
                  zIndex: 1,
                  '&:hover': {
                    '& .lesson-title': {
                      color: '#e53935'
                    }
                  }
                }}
              >
                {/* Lesson Dot Indicator */}
                <Box sx={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  border: '1px solid #c4c4c4',
                  background: '#fff',
                  mr: '12px',
                  ml: '14px',
                  mt: '2px',
                  flexShrink: 0,
                  ...(activeLesson === lesson.id && {
                    borderColor: '#e53935',
                    background: '#e53935'
                  })
                }} />

                {/* Lesson Content */}
                <Box>
                  <Box sx={{ 
                    fontSize: '12px', 
                    color: '#999',
                    lineHeight: '1.2'
                  }}>
                    Lesson {index + 1}
                  </Box>
                  <Box 
                    className="lesson-title"
                    sx={{ 
                      fontSize: '14px',
                      color: '#333',
                      transition: 'color 0.2s ease',
                      ...(activeLesson === lesson.id && {
                        color: '#e53935',
                        fontWeight: 'bold'
                      })
                    }}
                  >
                    {lesson.title}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}